import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
    Mic,
    MicOff,
    Send,
    Square,
    ArrowLeft,
    Volume2,
    Trophy,
    Sparkles,
    RefreshCw,
    Play,
    X,
    BarChart3,
} from "lucide-react";
import api, { API_BASE } from "../lib/api";
import { useConfirm } from "../components/ConfirmDialog";

function PulsingAvatar({ color = "#EE722E", isSpeaking = false, name }) {
    return (
        <div className="relative inline-flex items-center justify-center">
            <motion.div
                animate={
                    isSpeaking
                        ? { scale: [1, 1.15, 1], opacity: [0.4, 0.7, 0.4] }
                        : { scale: 1, opacity: 0.3 }
                }
                transition={{
                    duration: 1.2,
                    repeat: isSpeaking ? Infinity : 0,
                    ease: "easeInOut",
                }}
                className="absolute inset-0 rounded-full blur-2xl"
                style={{ background: color }}
            />
            <motion.div
                animate={
                    isSpeaking
                        ? { scale: [1, 1.05, 1] }
                        : { scale: 1 }
                }
                transition={{
                    duration: 1.2,
                    repeat: isSpeaking ? Infinity : 0,
                    ease: "easeInOut",
                }}
                className="relative w-32 h-32 lg:w-40 lg:h-40 rounded-full flex items-center justify-center font-extrabold text-shariti-black text-5xl lg:text-6xl"
                style={{
                    background: `linear-gradient(135deg, ${color}, ${color}cc)`,
                    boxShadow: `0 0 40px ${color}66, inset 0 0 24px rgba(255,255,255,0.15)`,
                }}
            >
                {name?.[0]?.toUpperCase()}
            </motion.div>
        </div>
    );
}

export default function SimulatorSession() {
    const { id } = useParams();
    const navigate = useNavigate();
    const confirm = useConfirm();
    const [session, setSession] = useState(null);
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [audioOn, setAudioOn] = useState(true);
    const [recording, setRecording] = useState(false);
    const [transcribing, setTranscribing] = useState(false);
    const [aiSpeaking, setAiSpeaking] = useState(false);
    const [ending, setEnding] = useState(false);
    const audioRef = useRef(null);
    const messagesEndRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const lastSpokenRef = useRef("");
    // iter-21: user can close the score modal to review the conversation + replay audio.
    const [scoreOpen, setScoreOpen] = useState(true);
    const [replayingIdx, setReplayingIdx] = useState(null);

    const speakText = useCallback(
        async (textToSpeak, voice, opts = {}) => {
            const force = opts.force === true;
            if (!textToSpeak) return;
            if (!force && !audioOn) return;
            if (!force && lastSpokenRef.current === textToSpeak) return;
            lastSpokenRef.current = textToSpeak;

            // Iter-29 — Stop net du lecteur audio avant tout nouveau play().
            //   Sans cette étape, `audioRef.current.src = url; play()` provoque
            //   un DOMException AbortError silencieux ET un "fade-out brutal"
            //   de la voix précédente (audible quand l'utilisateur enchaîne
            //   plusieurs sessions/profils rapidement).
            const audioEl = audioRef.current;
            if (audioEl) {
                try {
                    audioEl.pause();
                    audioEl.currentTime = 0;
                    if (audioEl.src && audioEl.src.startsWith("blob:")) {
                        // Libère l'URL blob précédente pour ne pas fuiter de mémoire
                        URL.revokeObjectURL(audioEl.src);
                    }
                    audioEl.removeAttribute("src");
                    audioEl.load();
                } catch (_) {
                    /* ignore */
                }
            }
            setAiSpeaking(false);

            try {
                const fd = new FormData();
                fd.append("text", textToSpeak);
                fd.append("voice", voice || "nova");
                // Iter-27.53 — Préfère la voix ElevenLabs FR native du profil si dispo.
                // Si pas définie, le backend tombe en fallback OpenAI legacy.
                const elevenVoice = opts.elevenlabsVoiceId
                    || session?.profile_elevenlabs_voice_id;
                if (elevenVoice) {
                    fd.append("elevenlabs_voice_id", elevenVoice);
                }
                const res = await fetch(`${API_BASE}/simulator/tts`, {
                    method: "POST",
                    body: fd,
                    credentials: "include",
                });
                if (!res.ok) return;
                const blob = await res.blob();
                const url = URL.createObjectURL(blob);
                if (audioRef.current) {
                    audioRef.current.src = url;
                    // Iter-29 — `play()` peut rejeter (AbortError) si l'élément
                    // a été interrompu entre-temps. On consomme la rejection
                    // proprement plutôt que de laisser un unhandled promise.
                    audioRef.current.play().catch((err) => {
                        if (err?.name !== "AbortError") {
                            console.warn("audio play() rejected", err);
                        }
                    });
                    setAiSpeaking(true);
                }
            } catch (e) {
                console.warn("TTS failed", e);
            }
        },
        [audioOn, session?.profile_elevenlabs_voice_id],
    );

    // Iter-29 — À l'unmount (retour vers /simulator), on coupe NET tout audio
    // en cours pour éviter qu'une voix de donateur persiste dans l'app après
    // qu'on a quitté la session. C'était la cause principale du ressenti
    // "la voix continue puis bascule" en changeant de profil.
    useEffect(() => {
        return () => {
            const el = audioRef.current;
            if (el) {
                try {
                    el.pause();
                    if (el.src && el.src.startsWith("blob:")) {
                        URL.revokeObjectURL(el.src);
                    }
                    el.removeAttribute("src");
                    el.load();
                } catch (_) { /* ignore */ }
            }
            lastSpokenRef.current = "";
        };
    }, []);

    const replayMessage = useCallback(
        async (idx, text) => {
            if (replayingIdx !== null) return;
            setReplayingIdx(idx);
            try {
                await speakText(text, session?.profile_voice, { force: true });
            } catch {
                setReplayingIdx(null);
            }
            // onEnded on the <audio> element clears replayingIdx when playback finishes
        },
        [replayingIdx, session?.profile_voice, speakText],
    );

    // Initial load
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/simulator/sessions/${id}`);
                setSession(data);
                // iter-21: If the session is already ended, we're in replay mode.
                // Open the score modal so the user lands on the feedback screen,
                // but keep all messages visible and allow closing the modal.
                if (data.ended_at) {
                    setScoreOpen(true);
                    return;
                }
                // If there's a session opening from start endpoint, replay last AI message
                if (data.messages?.length === 0 && data.opening) {
                    const newMessages = [
                        { role: "ai", text: data.opening, ts: new Date().toISOString() },
                    ];
                    setSession({ ...data, messages: newMessages });
                    speakText(data.opening, data.profile_voice);
                } else if (
                    data.messages?.length === 1 &&
                    data.messages[0].role === "ai" &&
                    !data.ended_at
                ) {
                    // Iter-21: opener is now persisted; auto-play the single AI msg.
                    speakText(data.messages[0].text, data.profile_voice);
                }
            } catch {
                toast.error("Session introuvable");
                navigate("/simulator");
            }
        })();
    }, [id, navigate, speakText]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [session?.messages?.length]);

    const sendText = async (textToSend) => {
        if (!textToSend?.trim() || sending) return;
        setSending(true);
        const userMsg = {
            role: "user",
            text: textToSend,
            ts: new Date().toISOString(),
        };
        setSession((prev) => ({
            ...prev,
            messages: [...(prev?.messages || []), userMsg],
        }));
        setText("");
        try {
            const { data } = await api.post(`/simulator/sessions/${id}/message`, {
                text: textToSend,
            });
            const aiMsg = {
                role: "ai",
                text: data.reply,
                ts: new Date().toISOString(),
            };
            setSession((prev) => ({
                ...prev,
                messages: [...(prev?.messages || []), aiMsg],
            }));
            speakText(data.reply, session?.profile_voice);
        } catch {
            toast.error("L'IA n'a pas répondu, réessaye");
        } finally {
            setSending(false);
        }
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
            audioChunksRef.current = [];
            mr.ondataavailable = (e) => {
                if (e.data.size > 0) audioChunksRef.current.push(e.data);
            };
            mr.onstop = async () => {
                stream.getTracks().forEach((t) => t.stop());
                const blob = new Blob(audioChunksRef.current, { type: "audio/webm" });
                if (blob.size === 0) return;
                setTranscribing(true);
                try {
                    const fd = new FormData();
                    fd.append("file", blob, "audio.webm");
                    const { data } = await api.post("/simulator/stt", fd, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });
                    if (data.text?.trim()) {
                        await sendText(data.text.trim());
                    } else {
                        toast.error("Rien n'a été détecté");
                    }
                } catch {
                    toast.error("Transcription échouée");
                } finally {
                    setTranscribing(false);
                }
            };
            mediaRecorderRef.current = mr;
            mr.start();
            setRecording(true);
        } catch (e) {
            toast.error("Micro inaccessible — autorise le micro dans ton navigateur");
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current?.state === "recording") {
            mediaRecorderRef.current.stop();
            setRecording(false);
        }
    };

    const endSession = async () => {
        const ok = await confirm({
            title: "Terminer la session ?",
            description:
                "L'IA va analyser votre échange et calculer un score (pitch, objections, closing) avec un feedback personnalisé. Tu ne pourras plus reprendre cette conversation après.",
            confirmLabel: "Obtenir mon score",
        });
        if (!ok) return;
        setEnding(true);
        try {
            const { data } = await api.post(`/simulator/sessions/${id}/end`);
            setSession(data);
            setScoreOpen(true);
            toast.success(`Session terminée — ${data.score?.overall || 0}%`);
            if (data.points_earned > 0) {
                window.dispatchEvent(new CustomEvent("shariti:points-changed"));
            }
        } catch {
            toast.error("Erreur");
        } finally {
            setEnding(false);
        }
    };

    if (!session) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-shariti-deep-black">
                <div className="w-10 h-10 rounded-full border-4 border-shariti-orange border-t-transparent animate-spin" />
            </div>
        );
    }

    const ended = !!session.ended_at;
    const score = session.score;

    return (
        <div className="min-h-screen bg-shariti-deep-black flex flex-col relative overflow-hidden">
            <div className="pointer-events-none fixed -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-shariti-orange/15 blur-[120px] animate-blob" />

            <audio
                ref={audioRef}
                onPlay={() => setAiSpeaking(true)}
                onEnded={() => {
                    setAiSpeaking(false);
                    setReplayingIdx(null);
                }}
                onPause={() => setAiSpeaking(false)}
            />

            {/* Header */}
            <header className="relative z-10 flex items-center justify-between px-4 lg:px-8 py-4 border-b border-white/5">
                <button
                    onClick={() => navigate("/simulator")}
                    data-testid="simulator-back"
                    className="flex items-center gap-2 text-white/60 hover:text-white"
                >
                    <ArrowLeft className="w-5 h-5" />
                    <span className="text-sm font-bold hidden sm:inline">Retour</span>
                </button>
                <div className="text-center">
                    <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light">
                        Profil donateur
                    </p>
                    <p className="text-sm lg:text-base font-extrabold text-white truncate max-w-[200px] lg:max-w-none">
                        {session.profile_name}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {ended && (
                        <button
                            onClick={() => setScoreOpen(true)}
                            data-testid="simulator-reopen-score"
                            className="hidden sm:inline-flex items-center gap-1.5 px-3 h-10 rounded-2xl bg-shariti-yellow/90 text-shariti-black font-extrabold text-xs uppercase tracking-widest hover:bg-shariti-yellow transition-all"
                            title="Voir le score et le feedback"
                        >
                            <BarChart3 className="w-4 h-4" />
                            Score
                        </button>
                    )}
                    <button
                        onClick={() => setAudioOn((v) => !v)}
                        data-testid="simulator-audio-toggle"
                        className={`w-10 h-10 rounded-2xl flex items-center justify-center transition-all ${
                            audioOn
                                ? "bg-shariti-orange text-white"
                                : "bg-white/5 text-white/40"
                        }`}
                        title={audioOn ? "Couper le son" : "Activer le son"}
                    >
                        {audioOn ? (
                            <Volume2 className="w-4 h-4" />
                        ) : (
                            <Volume2 className="w-4 h-4" />
                        )}
                    </button>
                </div>
            </header>

            {/* Avatar + last AI message */}
            <div className="relative z-10 flex flex-col items-center pt-8 pb-4 px-4">
                <PulsingAvatar
                    color={session.profile_avatar_color}
                    isSpeaking={aiSpeaking}
                    name={session.profile_name}
                />
                {/* Iter-29 — Badge "Mission" affiché si la session est ancrée à une asso */}
                {session.association_name && (
                    <div
                        className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-shariti-orange/15 border border-shariti-orange/30"
                        data-testid="simulator-asso-badge"
                    >
                        <Sparkles className="w-3 h-3 text-shariti-orange-light" />
                        <p className="text-[10px] font-extrabold tracking-[0.18em] uppercase text-shariti-orange-light">
                            Mission · {session.association_name}
                        </p>
                    </div>
                )}
                <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light mt-4 mb-1">
                    {aiSpeaking ? "parle..." : "écoute"}
                </p>
            </div>

            {/* Messages */}
            <div className="relative z-10 flex-1 overflow-y-auto px-4 lg:px-0 pb-4">
                <div
                    className="max-w-2xl mx-auto space-y-3"
                    data-testid="simulator-messages"
                >
                    {session.messages?.map((m, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                        >
                            <div
                                className={`max-w-[85%] group relative ${
                                    m.role === "user"
                                        ? "bg-shariti-orange text-white rounded-2xl rounded-tr-sm px-4 py-2.5"
                                        : "glass-card text-white rounded-2xl rounded-tl-sm px-4 py-2.5 pr-12"
                                }`}
                            >
                                <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                                    {m.text}
                                </p>
                                {m.role === "ai" && (
                                    <button
                                        onClick={() => replayMessage(i, m.text)}
                                        data-testid={`simulator-replay-${i}`}
                                        disabled={replayingIdx !== null}
                                        className={`absolute top-1 right-1 w-8 h-8 rounded-xl flex items-center justify-center transition-all ${
                                            replayingIdx === i
                                                ? "bg-shariti-orange text-white"
                                                : "bg-white/5 text-white/50 hover:bg-shariti-orange hover:text-white"
                                        } disabled:opacity-60`}
                                        title="Réécouter"
                                    >
                                        {replayingIdx === i ? (
                                            <Volume2 className="w-3.5 h-3.5" />
                                        ) : (
                                            <Play className="w-3 h-3 fill-current" />
                                        )}
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    ))}
                    {sending && (
                        <div className="flex justify-start">
                            <div className="glass-card rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-1">
                                <span className="w-1.5 h-1.5 bg-shariti-orange rounded-full animate-pulse" />
                                <span className="w-1.5 h-1.5 bg-shariti-orange rounded-full animate-pulse [animation-delay:0.2s]" />
                                <span className="w-1.5 h-1.5 bg-shariti-orange rounded-full animate-pulse [animation-delay:0.4s]" />
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* End-of-session score — iter-21: closable (backdrop click + X) */}
            <AnimatePresence>
                {ended && score && scoreOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setScoreOpen(false)}
                        className="fixed inset-0 z-50 bg-shariti-black/90 backdrop-blur flex items-end sm:items-center justify-center p-4 overflow-y-auto"
                        data-testid="simulator-score-modal"
                    >
                        <motion.div
                            initial={{ y: 80, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 40, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full max-w-xl bg-shariti-surface rounded-3xl p-6 lg:p-8 border border-white/10 max-h-[92vh] overflow-y-auto my-auto relative"
                        >
                            <button
                                onClick={() => setScoreOpen(false)}
                                data-testid="simulator-score-close"
                                className="absolute top-3 right-3 w-9 h-9 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white transition-all"
                                title="Fermer et voir la conversation"
                            >
                                <X className="w-4 h-4" />
                            </button>
                            <div className="text-center mb-5">
                                <Trophy className="w-12 h-12 mx-auto text-shariti-yellow mb-3" />
                                <p className="text-xs font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-1">
                                    Score global
                                </p>
                                <p className="text-6xl font-extrabold text-white">
                                    {score.overall || 0}
                                    <span className="text-2xl text-white/40">%</span>
                                </p>
                                {session.points_earned != null && (
                                    <p className="text-shariti-orange font-bold mt-2">
                                        +{session.points_earned} pts
                                    </p>
                                )}
                            </div>
                            <div className="grid grid-cols-3 gap-2 mb-5">
                                {[
                                    { key: "pitch", label: "Pitch" },
                                    { key: "objections", label: "Objections" },
                                    { key: "closing", label: "Closing" },
                                ].map((s) => (
                                    <div
                                        key={s.key}
                                        className="glass-card rounded-2xl p-3 text-center"
                                    >
                                        <p className="text-[10px] font-thin uppercase tracking-widest text-white/40 mb-1">
                                            {s.label}
                                        </p>
                                        <p className="text-xl font-extrabold text-white">
                                            {score[s.key] || 0}%
                                        </p>
                                    </div>
                                ))}
                            </div>
                            {score.feedback && (
                                <div className="glass-card rounded-2xl p-4 mb-4">
                                    <p className="text-[10px] font-thin uppercase tracking-widest text-shariti-orange-light mb-2">
                                        Feedback du coach
                                    </p>
                                    <p className="text-sm text-white/80 leading-relaxed">
                                        {score.feedback}
                                    </p>
                                </div>
                            )}
                            {score.improvements?.length > 0 && (
                                <div className="space-y-1.5 mb-6">
                                    <p className="text-[10px] font-thin uppercase tracking-widest text-shariti-orange-light mb-2">
                                        Axes d'amélioration
                                    </p>
                                    {score.improvements.map((imp, i) => (
                                        <div
                                            key={i}
                                            className="glass-card rounded-xl p-3 flex items-start gap-2"
                                        >
                                            <Sparkles className="w-4 h-4 text-shariti-orange shrink-0 mt-0.5" />
                                            <p className="text-sm text-white/80">{imp}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="flex gap-2">
                                <button
                                    onClick={() => setScoreOpen(false)}
                                    data-testid="simulator-view-conversation"
                                    className="flex-1 glass-card rounded-2xl py-3 font-bold text-white hover:bg-white/5 transition-all"
                                >
                                    Voir la conversation
                                </button>
                                <button
                                    onClick={() => navigate("/simulator")}
                                    data-testid="simulator-new-session"
                                    className="flex-1 inline-flex items-center justify-center gap-2 bg-shariti-orange text-white font-extrabold py-3 rounded-2xl orange-glow hover:bg-shariti-orange-light transition-all"
                                >
                                    <RefreshCw className="w-4 h-4" />
                                    Nouvelle session
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Composer */}
            {!ended && (
                <div className="relative z-10 px-4 pb-4 pt-3 border-t border-white/5">
                    <div className="max-w-2xl mx-auto flex items-end gap-2">
                        <button
                            onClick={recording ? stopRecording : startRecording}
                            disabled={transcribing || sending}
                            data-testid="simulator-mic-button"
                            className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all shrink-0 ${
                                recording
                                    ? "bg-red-500 text-white animate-pulse"
                                    : transcribing
                                    ? "bg-white/5 text-white/40"
                                    : "bg-shariti-orange text-white orange-glow hover:bg-shariti-orange-light"
                            }`}
                            title={recording ? "Arrêter" : "Parler"}
                        >
                            {recording ? (
                                <Square className="w-4 h-4 fill-current" />
                            ) : transcribing ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                                <Mic className="w-5 h-5" />
                            )}
                        </button>
                        <textarea
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    sendText(text);
                                }
                            }}
                            placeholder={
                                recording
                                    ? "Enregistrement..."
                                    : "Tape ou clique sur le micro..."
                            }
                            rows={1}
                            data-testid="simulator-text-input"
                            disabled={recording}
                            className="flex-1 bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-white placeholder-white/30 resize-none outline-none focus:border-shariti-orange/60"
                        />
                        <button
                            onClick={() => sendText(text)}
                            disabled={!text.trim() || sending}
                            data-testid="simulator-send-button"
                            className="w-12 h-12 rounded-2xl bg-shariti-orange text-white flex items-center justify-center hover:bg-shariti-orange-light disabled:opacity-40 transition-all shrink-0"
                        >
                            <Send className="w-4 h-4" />
                        </button>
                    </div>
                    {session.messages?.length >= 4 && (
                        <div className="max-w-2xl mx-auto mt-3 text-center">
                            <button
                                onClick={endSession}
                                disabled={ending}
                                data-testid="simulator-end-button"
                                className="text-xs font-bold text-white/40 hover:text-shariti-orange uppercase tracking-widest"
                            >
                                {ending ? "Calcul du score..." : "Terminer & obtenir le score"}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
